import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";

/**
 * Animated brand wordmark for "Signature Shine Co."
 * variant:
 *   - "hero"   → giant cinematic reveal (used on landing hero)
 *   - "navbar" → compact, subtle motion on hover (used in Navbar/Footer)
 */
export default function BrandMark({ variant = "navbar", className = "" }) {
    const isHero = variant === "hero";

    if (isHero) {
        const word = "SHINE CO.";
        return (
            <div className={`relative inline-block ${className}`} data-testid="brandmark-hero">
                {/* Rotating gold ring + sparkle */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.6 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className="flex items-center gap-4 mb-2"
                >
                    <div className="relative w-14 h-14">
                        <div className="absolute inset-0 rounded-full border border-gold/60 animate-ring-rotate" />
                        <div className="absolute inset-1 rounded-full border border-gold/30" style={{ animation: "ring-rotate 24s linear infinite reverse" }} />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <Sparkles className="w-6 h-6 text-gold animate-sparkle-spin" />
                        </div>
                    </div>
                    <div className="h-px w-16 bg-gradient-to-r from-gold to-transparent" />
                </motion.div>

                {/* Signature script — cursive sweep */}
                <motion.span
                    initial={{ opacity: 0, x: -50, skewX: -10 }}
                    animate={{ opacity: 1, x: 0, skewX: 0 }}
                    transition={{ duration: 1.1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                    className="font-script text-6xl md:text-7xl lg:text-8xl text-gold leading-none block mb-2 animate-gold-glow"
                    style={{ animationDelay: "1.4s" }}
                >
                    Signature
                </motion.span>

                {/* SHINE CO. — letters cascade */}
                <h1 className="font-playfair text-5xl md:text-6xl lg:text-7xl text-cream tracking-tight leading-tight flex flex-wrap">
                    {word.split("").map((ch, i) => (
                        <motion.span
                            key={i}
                            initial={{ opacity: 0, y: -30, rotate: -8 }}
                            animate={{ opacity: 1, y: 0, rotate: 0 }}
                            transition={{
                                duration: 0.6,
                                delay: 0.9 + i * 0.06,
                                ease: [0.34, 1.56, 0.64, 1],
                            }}
                            className="inline-block"
                            style={{ whiteSpace: ch === " " ? "pre" : "normal" }}
                        >
                            {ch}
                        </motion.span>
                    ))}
                </h1>
            </div>
        );
    }

    // Navbar / footer compact version
    return (
        <motion.div
            whileHover={{ scale: 1.04 }}
            transition={{ type: "spring", stiffness: 300, damping: 18 }}
            className={`flex items-center gap-3 group cursor-pointer ${className}`}
            data-testid="brandmark-compact"
        >
            <div className="relative w-10 h-10">
                <div className="absolute inset-0 rounded-full border border-gold flex items-center justify-center bg-navy">
                    <Sparkles className="w-5 h-5 text-gold group-hover:animate-sparkle-spin" />
                </div>
                <div className="absolute -inset-0.5 rounded-full border border-gold/30 animate-ring-rotate opacity-60" />
            </div>
            <div className="flex flex-col leading-none">
                <span className="font-script text-2xl text-gold -mb-1 transition-all group-hover:text-gold-light">Signature</span>
                <span className="font-playfair text-[10px] tracking-[0.3em] text-cream uppercase">Shine Co.</span>
            </div>
        </motion.div>
    );
}
